import {HttpClient, provideHttpClient, withInterceptors} from '@angular/common/http';
import {mailboxAuthInterceptor} from './modules/admin/mailbox/services/mailbox-auth.interceptor';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    DEFAULT_CURRENCY_CODE,
    importProvidersFrom,
    inject,
    LOCALE_ID
} from '@angular/core';
import {LuxonDateAdapter} from '@angular/material-luxon-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {BrowserAnimationsModule, provideAnimations} from '@angular/platform-browser/animations';
import {PreloadAllModules, provideRouter, TitleStrategy, withInMemoryScrolling, withPreloading} from '@angular/router';
import {NahausTitleStrategy} from './core/title/nahaus-title.strategy';
import {provideTransloco, TranslocoService} from '@ngneat/transloco';
import {firstValueFrom} from 'rxjs';
// import {appRoutes} from 'app/app.routes';
// import {provideIcons} from 'app/core/icons/icons.provider';
import {TranslocoHttpLoader} from './core/transloco/transloco.http-loader';
import * as moment from "moment";
import {appRoutes} from "./app.routing";
import {provideFuse} from "@fuse/fuse.provider";
import {mockApiServices} from "./mock-api";
import {environment} from "@env/environment";
import {CustomDateAdapter} from "./app.component";
import {Platform} from "@angular/cdk/platform";
import {
    AngularFireAnalyticsModule,
    APP_NAME,
    APP_VERSION,
    ScreenTrackingService,
    UserTrackingService
} from "@angular/fire/compat/analytics";
import {AngularFireFunctionsModule, REGION} from "@angular/fire/compat/functions";
import {LANGUAGE_CODE, USE_DEVICE_LANGUAGE} from "@angular/fire/compat/auth";
import {MatPaginatorIntl} from "@angular/material/paginator";
import {MyCustomPaginatorIntl} from "@shared/classes/MyCustomPaginatorIntl";
import {MAT_CHIPS_DEFAULT_OPTIONS} from "@angular/material/chips";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {AngularFirePerformanceModule, PerformanceMonitoringService} from "@angular/fire/compat/performance";
import {getApp, initializeApp, provideFirebaseApp} from "@angular/fire/app";
import {connectFirestoreEmulator, initializeFirestore, provideFirestore} from "@angular/fire/firestore";
import {getMessaging, provideMessaging} from "@angular/fire/messaging";
import {getAuth, provideAuth} from "@angular/fire/auth";
import {getStorage, provideStorage} from "@angular/fire/storage";
import {BrowserModule} from "@angular/platform-browser";
import {CurrencyPipe, DatePipe, DecimalPipe, NgOptimizedImage} from "@angular/common";
import {AngularFireModule} from "@angular/fire/compat";
import {AngularFirestoreModule} from "@angular/fire/compat/firestore";
import {AngularFireStorageModule} from "@angular/fire/compat/storage";
import {FuseMockApiModule} from "@fuse/lib/mock-api";
import {MatGoogleMapsAutocompleteModule} from "@angular-material-extensions/google-maps-autocomplete";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {MomentModule} from "ngx-moment";
import {NgxWebstorageModule} from "ngx-webstorage";
import {QuillModule} from "ngx-quill";
import {GoogleTagManagerModule} from "angular-google-tag-manager";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {StoreModule} from "@ngrx/store";
import {appReducer} from "@nahausStore/reducers/app.reducer";
import {EffectsModule} from "@ngrx/effects";
import {AuthEffects} from "@nahausStore/effects/auth.effects";
import {StoreDevtoolsModule} from "@ngrx/store-devtools";
import {ServiceWorkerModule} from "@angular/service-worker";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {ToDatePipe} from "@shared/pipes";
import {provideIcons} from './core/icons/icons.provider';
import {SAVER} from "@shared/download/saver.provide";
import {MsalBroadcastService, MsalGuard, MsalService} from '@azure/msal-angular';


import {getFunctions, provideFunctions} from '@angular/fire/functions';

const googleMapsParams = {
    apiKey: environment.GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    language: 'de',
    region: 'DE'
};

// AoT requires an exported function for factories
export const HttpLoaderFactory = (http: HttpClient): TranslateHttpLoader => new TranslateHttpLoader(http);

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withInterceptors([mailboxAuthInterceptor])),
        provideRouter(appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
        ),
        // Browser-Tab-Titel je Route: „Immobilien | Nahaus“ (siehe core/title/nahaus-title.strategy.ts)
        {provide: TitleStrategy, useClass: NahausTitleStrategy},
        {
            provide: SAVER,
            useValue: SAVER
        },


        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        // {
        //     provide: MAT_DATE_FORMATS,
        //     useValue: {
        //         parse: {
        //             dateInput: 'D',
        //         },
        //         display: {
        //             dateInput: 'DDD',
        //             monthYearLabel: 'LLL yyyy',
        //             dateA11yLabel: 'DD',
        //             monthYearA11yLabel: 'LLLL yyyy',
        //         },
        //     },
        // },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: moment.ISO_8601
                },
                display: {
                    dateInput: 'LL',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY'
                }
            }
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'de',
                        label: 'Deutsch'
                    },
                    {
                        id: 'en',
                        label: 'English'
                    },
                ],
                defaultLang: 'de',
                fallbackLang: 'de',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },

        // Fuse
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        // provideAuth(),

        // Firebase Modular
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideFirestore(() => {
            const firestore = initializeFirestore(getApp(), {
                // localCache: persistentLocalCache({
                //     tabManager: persistentMultipleTabManager()
                // }),
            });
            // persistentMultipleTabManager();
            if (environment.useEmulators) {
                connectFirestoreEmulator(firestore, 'localhost', 8080);
            }
            return firestore;
        }),
        provideMessaging(() => getMessaging()),
        provideAuth(() => getAuth()),
        provideStorage(() => getStorage()),
        provideFunctions(() => {
            // ggf. Region angeben
            const fns = getFunctions(undefined, 'europe-west3');
            // optional Emulator:
            // if (!environment.production) connectFunctionsEmulator(fns, 'localhost', 5001);
            return fns;
        }),

        // Firebase compact
        PerformanceMonitoringService,
        ScreenTrackingService,
        UserTrackingService,


        // firebase emulators
        // {provide: USE_AUTH_EMULATOR, useValue: environment.useEmulators ? ['http://localhost:9099'] : undefined},
        // {provide: USE_FIRESTORE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 8080] : undefined},
        // {provide: USE_FUNCTIONS_EMULATOR, useValue: environment.useEmulators ? ['localhost', 5001] : undefined},
        // {provide: USE_STORAGE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 9199] : undefined},

        // additional providers
        {provide: LOCALE_ID, useValue: 'de'},
        {provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE, Platform]},
        {provide: APP_NAME, useValue: environment.customer},
        {provide: APP_VERSION, useValue: environment.version},
        // { provide: USE_DEVICE_LANGUAGE, useValue: false },
        {provide: REGION, useValue: 'europe-west3'},
        {provide: LANGUAGE_CODE, useValue: 'de'},
        {provide: USE_DEVICE_LANGUAGE, useValue: true},
        {provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR'},
        {provide: MatPaginatorIntl, useClass: MyCustomPaginatorIntl},
        importProvidersFrom(BrowserModule.withServerTransition({appId: 'serverApp'}),
            // RouterModule.forRoot(appRoutes, routerConfig),
            NgOptimizedImage,
            // Angular Fire
            AngularFireModule.initializeApp(environment.firebase),
            // AngularFirestoreModule.enablePersistence(),
            AngularFirestoreModule,
            AngularFireFunctionsModule,
            AngularFireStorageModule,
            AngularFirePerformanceModule,
            AngularFireAnalyticsModule,
            // provideFirestore(() => {
            //     const firestore = getFirestore();
            //
            //     // persistentMultipleTabManager();
            //     if (environment.useEmulators) {
            //         connectFirestoreEmulator(firestore, 'localhost', 8080);
            //     }
            //
            //     return firestore;
            // }),
            // provideAppCheck(() =>
            //   initializeAppCheck(undefined, {
            //     provider: new ReCaptchaV3Provider(environment.reCaptchaV3Key),
            //     isTokenAutoRefreshEnabled: true
            //   })
            // ),


            // Fuse, FuseConfig & FuseMockAPI
            FuseMockApiModule.forRoot(mockApiServices),
            // NEW FOR STANDALONE COMPONENTS
            // 3rd party modules
            MatGoogleMapsAutocompleteModule.forRoot(environment.GOOGLE_MAPS_API_KEY), TranslateModule.forRoot({
                useDefaultLang: true,
                defaultLanguage: 'de',
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient]
                }
            }),
            MomentModule.forRoot(),
            NgxWebstorageModule.forRoot(),
            QuillModule.forRoot(),
            GoogleTagManagerModule.forRoot({
                id: environment.GTM_ID
            }),
            BrowserAnimationsModule,

            // Material
            MatSnackBarModule,

            // Ngrx
            StoreModule.forRoot({app: appReducer}),
            EffectsModule.forRoot([AuthEffects]),
            StoreDevtoolsModule.instrument({
                maxAge: 25,
                logOnly: environment.production
            }),
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: environment.production,
                // Register the ServiceWorker as soon as the application is stable
                // or after 30 seconds (whichever comes first).
                registrationStrategy: 'registerWhenStable:30000'
            })
        ),

        {
            provide: MAT_CHIPS_DEFAULT_OPTIONS,
            useValue: {
                separatorKeyCodes: [ENTER, COMMA]
            }
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        CurrencyPipe,
        ToDatePipe,
        DatePipe,
        DecimalPipe,
    ],
};
